import posthog, { type CaptureResult } from "posthog-js";
import { routePattern } from "./telemetry/route";
import { VERSAO_ATUAL } from "./versao";

// List filters live in the query string (?f=), and a search term or a salary range must not
// leave the browser: every URL sent to PostHog keeps only its path.
const URL_PROPERTIES = [
  "$current_url",
  "$referrer",
  "$initial_current_url",
  "$initial_referrer",
  "$session_entry_url",
  "$session_entry_referrer",
  "$prev_pageview_url",
];

// The page title can carry a person's name.
const FREE_TEXT_PROPERTIES = ["title", "$title"];

export function stripQueryAndHash(url: string): string {
  const cut = url.search(/[?#]/);
  return cut === -1 ? url : url.slice(0, cut);
}

function sanitizeProperties(properties: Record<string, unknown> | undefined): void {
  if (!properties) return;
  for (const name of URL_PROPERTIES) {
    const value = properties[name];
    if (typeof value === "string") properties[name] = stripQueryAndHash(value);
  }
  for (const name of FREE_TEXT_PROPERTIES) delete properties[name];
}

const VAGA_QUERY_PARAMETERS = ["vaga", "fromVaga"];
const UUID = "[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}";
const VAGA_ID = new RegExp(`^${UUID}$`, "i");
const VAGA_PATH = new RegExp(`/vagas/(${UUID})(?:/|$)`, "i");

/** The vaga a screen works for: the list in vaga mode and a profile opened from it say so in the query, the vaga's own pages in the path. */
export function vagaIdFromUrl(url: string): string | undefined {
  try {
    const { pathname, searchParams } = new URL(url, "https://app.invalid");
    const fromQuery = VAGA_QUERY_PARAMETERS.map((name) => searchParams.get(name)).find((value) => value && VAGA_ID.test(value));
    return fromQuery ?? VAGA_PATH.exec(pathname)?.[1];
  } catch {
    return undefined;
  }
}

export function sanitizeEvent(event: CaptureResult | null): CaptureResult | null {
  if (!event) return null;
  // Read before the URL loses its query below. A call site that knows the vaga has already set it.
  if (!event.properties.vaga_id && typeof event.properties.$current_url === "string") {
    const vagaId = vagaIdFromUrl(event.properties.$current_url);
    if (vagaId) event.properties.vaga_id = vagaId;
  }
  sanitizeProperties(event.properties);
  sanitizeProperties(event.$set);
  sanitizeProperties(event.$set_once);
  // Set per event, not registered: both versions share an origin, so a persisted value could
  // cross from one version's tab to the other's.
  event.properties.versao = VERSAO_ATUAL;
  if (event.event === "$pageview" && typeof event.properties.$pathname === "string") {
    event.properties.tela = screenOf(event.properties.$pathname);
  }
  return event;
}

/** The screen as a route pattern, the same value `tela` has on every other event. */
function screenOf(pathname: string): string {
  const base = import.meta.env.BASE_URL.replace(/\/$/, "");
  const path = base && pathname.startsWith(base) ? pathname.slice(base.length) || "/" : pathname;
  return routePattern(path);
}

// The recorder masks text and inputs but records attributes as they are, and a title or an
// aria-label can carry a person's name. Only attributes that draw the page go through.
const REPLAY_VISIBLE_ATTRIBUTES = new Set([
  "class", "style", "id", "role", "type", "tabindex", "rel", "media", "charset", "lang", "dir",
  "width", "height", "viewBox", "xmlns", "d", "fill", "stroke", "stroke-width", "stroke-linecap",
  "stroke-linejoin", "cx", "cy", "r", "x", "y", "x1", "x2", "y1", "y2", "rx", "ry", "points",
  "transform", "data-state", "data-side", "data-align", "data-orientation", "data-sidebar",
  "data-active", "data-size", "data-theme", "data-variant",
]);
const REPLAY_URL_ATTRIBUTES = new Set(["href", "src"]);

export function maskReplayAttribute(name: string, value: string): string {
  if (REPLAY_URL_ATTRIBUTES.has(name)) return stripQueryAndHash(value);
  // Attributes the recorder adds itself (rr_width, _cssText), not read from the page.
  if (name.startsWith("rr_") || name.startsWith("_")) return value;
  return REPLAY_VISIBLE_ATTRIBUTES.has(name) ? value : "***";
}

// The rule of POST /v3/eventos (api/app/core/eventos_uso.py): known keys only, and values that are
// codes, numbers or booleans, so free text never leaves the browser whatever a call site passes.
const EVENT_CONTEXT_KEYS = new Set([
  "fase", "etapa", "tipo", "item", "aba", "secao", "origem", "destino", "canal", "motivo", "nivel",
  "estado", "resposta", "ordenacao", "criterio", "acao_original", "motor_versao", "fonte", "modo",
  "metodo", "modulo", "rota", "filtros", "ids_tipo", "rank", "posicao", "pagina", "total",
  "quantidade", "filtros_n", "status", "score", "confianca", "ai_sugerido", "primeira", "com_vaga",
  "com_busca", "lote", "ligado", "filtros_adicionados", "filtros_removidos", "lista", "coluna",
  "vaga_id", "conversa_id", "contexto_tipo", "ferramentas",
]);
const CODE = /^[a-z0-9][a-z0-9_.:-]{0,59}$/;
const ROUTE = /^\/[A-Za-z0-9/:_-]{0,119}$/;
const ENTITY_ID = /^[A-Za-z0-9_:.-]{1,64}$/;
const MAX_LIST_ITEMS = 12;

function safeContextValue(value: unknown): boolean | number | string | string[] | undefined {
  if (typeof value === "boolean") return value;
  if (typeof value === "number") return Number.isFinite(value) ? value : undefined;
  if (typeof value === "string") return CODE.test(value) || ROUTE.test(value) ? value : undefined;
  if (!Array.isArray(value)) return undefined;
  return value.filter((item): item is string => typeof item === "string" && CODE.test(item)).slice(0, MAX_LIST_ITEMS);
}

const FILTER_PROPERTY_PREFIX = "filtro_";
const MAX_FILTER_VALUE_LENGTH = 80;

function safeFilterValue(value: unknown): string | string[] | undefined {
  if (typeof value === "string") return value ? value.slice(0, MAX_FILTER_VALUE_LENGTH) : undefined;
  if (!Array.isArray(value)) return undefined;
  const items = value
    .filter((item): item is string => typeof item === "string" && item !== "")
    .slice(0, MAX_LIST_ITEMS)
    .map((item) => item.slice(0, MAX_FILTER_VALUE_LENGTH));
  return items.length > 0 ? items : undefined;
}

/**
 * The values of the freelancer list's filters, one property per filter, so PostHog can break
 * results down by them. The list passes the text of its search box here too, as `busca`.
 */
export function filterValueProperties(filters: Record<string, unknown>): Record<string, string | string[]> {
  const properties: Record<string, string | string[]> = {};
  for (const [name, value] of Object.entries(filters)) {
    const safe = safeFilterValue(value);
    if (safe !== undefined) properties[FILTER_PROPERTY_PREFIX + name.toLowerCase().replace(/[^a-z0-9_]+/g, "_")] = safe;
  }
  return properties;
}

export type ProductEvent = {
  tela: string;
  entidadeTipo?: string;
  entidadeId?: string;
  resultado?: string;
  duracaoMs?: number;
  contexto?: Record<string, unknown>;
};

export function productEventProperties(event: ProductEvent): Record<string, unknown> {
  const properties: Record<string, unknown> = { tela: event.tela, resultado: event.resultado ?? "ok" };
  if (event.entidadeTipo) properties.entidade_tipo = event.entidadeTipo;
  if (event.entidadeId && ENTITY_ID.test(event.entidadeId)) properties.entidade_id = event.entidadeId;
  if (event.duracaoMs !== undefined) properties.duracao_ms = event.duracaoMs;
  for (const [key, value] of Object.entries(event.contexto ?? {})) {
    if (key.startsWith(FILTER_PROPERTY_PREFIX) && CODE.test(key)) {
      const filterValue = safeFilterValue(value);
      if (filterValue !== undefined) properties[key] = filterValue;
      continue;
    }
    if (!EVENT_CONTEXT_KEYS.has(key)) continue;
    const safe = safeContextValue(value);
    if (safe !== undefined) properties[key] = safe;
  }
  return properties;
}

let initialized = false;

// Screens fire their first events before permissions load, and the SDK drops whatever arrives
// before consent. They wait here, in memory only, until syncPostHogUser decides who this is.
const MAX_PENDING_EVENTS = 100;
type PendingEvent = { action: string; properties: Record<string, unknown>; timestamp: Date };
let pendingEvents: PendingEvent[] | null = [];

/** Sent once syncPostHogUser opts a signed-in internal user in, discarded for anyone else. Never throws. */
export function captureProductEvent(action: string, event: ProductEvent): void {
  if (!initialized) return;
  try {
    const properties = productEventProperties(event);
    if (!pendingEvents) {
      posthog.capture(action, properties);
    } else if (pendingEvents.length < MAX_PENDING_EVENTS) {
      pendingEvents.push({ action, properties, timestamp: new Date() });
    }
  } catch {
    // Analytics never breaks a screen.
  }
}

/** Nothing is captured until syncPostHogUser opts a signed-in internal user in. */
export function initPostHog(): void {
  const apiKey = import.meta.env.VITE_POSTHOG_KEY;
  const apiHost = import.meta.env.VITE_POSTHOG_HOST;
  if (import.meta.env.VITE_ENABLE_POSTHOG !== "true" || !apiKey || !apiHost) return;

  posthog.init(apiKey, {
    api_host: apiHost,
    opt_out_capturing_by_default: true,
    person_profiles: "identified_only",
    capture_pageview: "history_change",
    autocapture: false,
    rageclick: false,
    capture_dead_clicks: false,
    capture_heatmaps: false,
    capture_exceptions: false,
    capture_performance: false,
    disable_surveys: true,
    disable_product_tours: true,
    disable_web_experiments: true,
    before_send: sanitizeEvent,
    // The SDK remembers an opt-in and would send a pageview on the next load, before anyone
    // knows who this is. Consent lasts one page load: syncPostHogUser grants it again.
    loaded: (instance) => instance.opt_out_capturing(),
    // Off in the code as well as in the project: turning replay on takes a change here, and a
    // new check that the masking below still holds.
    disable_session_recording: true,
    session_recording: {
      maskAllInputs: true,
      maskTextSelector: "*",
      maskAttributeFn: maskReplayAttribute,
      // Masking text does not hide an image: its src is still recorded.
      blockSelector: "img, video",
      // The recorder also passes the page URL through this function.
      maskCapturedNetworkRequestFn: (request) => ({
        ...request,
        name: stripQueryAndHash(request.name),
      }),
    },
  });
  initialized = true;
}

export type PostHogUser = { id: string; isInternal: boolean } | null;

/** Identifies by id only, never a name or an e-mail. Agency users and signed-out visitors stay out. */
export function syncPostHogUser(user: PostHogUser): void {
  if (!initialized) return;
  const held = pendingEvents ?? [];
  pendingEvents = null;
  if (user?.isInternal) {
    posthog.opt_in_capturing({ captureEventName: null });
    posthog.identify(user.id);
    for (const { action, properties, timestamp } of held) posthog.capture(action, properties, { timestamp });
    return;
  }
  posthog.opt_out_capturing();
  if (!user) posthog.reset();
}
